import GridContainer from "@/components/common/GridContainer";
import styled from "styled-components";
import {getCssSizeValue, getHtmlFromRedactorField, getItemUrl, getPropSafely, isStringEmpty} from "@/utils/utils";
import DateContainer from "@/components/AnnouncementsAndTendersList/DateContainer";
import {insetGapLeftCss} from "@/styles/globalStyles";
import {getMappedCategoriesToSelectValues} from "@/components/common/AnnouncementFiltration";
import Text from "@/components/common/Text";
import SimpleIconButton, {
	SIMPLE_ICON_BUTTON_ANIMATIONS,
	SIMPLE_ICON_BUTTON_CLASS_NAME
} from "@/components/common/SimpleIconButton";
import {BREAKPOINTS, colors} from "@/styles/themeSettings";
import React, {useRef} from "react";
import useHover from "@/hooks/useHover";
import {useRouter} from "next/router";
import CommonLineBreak from "@/components/common/LineBreak";
import useDevice from "@/hooks/useDevice";

const Wrapper = styled.div`
    cursor: pointer;
    position: relative;
    transition: all var(--rgr-duration) var(--ease);
    padding: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(32, BREAKPOINTS.MOBILE)};

    border-bottom: 1px solid ${({theme}) => theme.colors.platinum};
	
    &:first-child {
        border-top: 1px solid ${({theme}) => theme.colors.platinum};
    }

    .date-container {
        translate: 0 -55px;
    }

    .right {
        ${insetGapLeftCss};
    }

    .first-row {
        margin-bottom: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
    }

    .third-row {
        margin-top: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
    }

    .${SIMPLE_ICON_BUTTON_CLASS_NAME} {
        position: absolute;
        top: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
        right: var(--paddingX);
    }

    &.hover {
        background: ${({theme}) => theme.colors.whiteSmoke};
    }
	
    @media (min-width: ${({theme}) => theme.breakpoints.tablet}px) {
    }

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        padding: ${getCssSizeValue(32)} 0 ${getCssSizeValue(64)};
		
		.first-row {
            margin-bottom: ${getCssSizeValue(42, BREAKPOINTS.DESKTOP)};
		}

        .third-row {
            margin-top: ${getCssSizeValue(16, BREAKPOINTS.DESKTOP)};
        }

        .${SIMPLE_ICON_BUTTON_CLASS_NAME} {
            top: ${getCssSizeValue(32, BREAKPOINTS.DESKTOP)};
        }
	}
`

const LineBreak = styled(CommonLineBreak)`
    display: none;
    background-color: ${({theme}) => theme.colors.antiflashWhite};

    &.active {
        background-color: ${({theme}) => theme.colors.black};

        &:after {
            transform: translateX(var(--paddingX)) scaleX(var(--target-scale, 1)) scaleY(var(--size)) !important;
        }
    }

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        margin-top: ${getCssSizeValue(64)};
        display: block;
    }
`

function getData(src) {
	return {
		bannerText: getPropSafely(src, 'bannerText'),
		description: getPropSafely(src, 'description'),
		fromDate: getPropSafely(src, 'fromDate'),
		toDate: getPropSafely(src, 'toDate'),
		slug: getPropSafely(src, 'slug'),
		pageType: getPropSafely(src, 'pageType'),
		year: getPropSafely(src, 'year', {}),
	}
}

export default function ListItem({item, parentSlug}) {
	const {isDesktop, isMobile} = useDevice()
	const router = useRouter()
	const mappedCategories = getMappedCategoriesToSelectValues([])
	const {bannerText, description, fromDate, toDate, slug, pageType, year} = getData(item)
	// const href = `${parentSlug}/${slug}`
	const href = getItemUrl(item)
	const category = mappedCategories.find((category) => category.value === pageType)
	const [ref, hover] = useHover()

	// logNamed({ url, href })
	const handleClick = () => {
		router.push(href)
	}

	const DateContainerRef = useRef()

	return (
		<Wrapper ref={ref} onClick={handleClick} className={hover ? 'hover' : ''}>
			<SimpleIconButton
				color={isDesktop ? colors.chilliRed : colors.white}
				bgColor={isDesktop ? colors.white : colors.chilliRed}
				borderColor={colors.chilliRed}
				rotation={135}
				animation={hover ? SIMPLE_ICON_BUTTON_ANIMATIONS.ROTATE_45 : null}
			/>
			{isDesktop && (
				<GridContainer className='first-row'>
					<Text
						className='square static typo-strawford typo-strawford-12 uppercase'
					>
						{category?.label || ''}
					</Text>
				</GridContainer>
			)}
			{isMobile && (
				<GridContainer className='first-row'>
					<Text
						as='div'
						className='square static typo-strawford typo-strawford-34 uppercase col-mobile-8'
					>
						{fromDate} - {toDate}
					</Text>
				</GridContainer>
			)}
			<GridContainer className='second-row'>
				<div
					ref={DateContainerRef}
					className='col-desktop-9 col-mobile-7 typo-borna typo-borna-128'
					dangerouslySetInnerHTML={getHtmlFromRedactorField(bannerText)}
				/>
				{isDesktop && <div className='col-desktop-1'/> && (
					<>
						<DateContainer date={fromDate} header={'Zapytanie z dnia'} className='col-desktop-2'/>
						<DateContainer date={toDate} header={'Składanie ofert do'} className='col-desktop-2 right'/>
					</>
				)}
			</GridContainer>
			{!isStringEmpty(description) && (
				<GridContainer className='third-row'>
					<div className='col-desktop-9 col-mobile-8 typo-strawford typo-strawford-14'
						 dangerouslySetInnerHTML={getHtmlFromRedactorField(description)}/>
				</GridContainer>
			)}
			<LineBreak active={hover} target={DateContainerRef} size={6}/>
		</Wrapper>
	)
}
